import { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { usePermissao } from '../security/usePermissao';
import { useBootstrap } from '../context/BootstrapContext';
import { configurarCliente, validarEmailCliente, updateClienteBranding } from '../infra/api/Clientes';

const MAX_IMAGE_BYTES = 1.5 * 1024 * 1024; // 1.5MB — leve o suficiente pro documento Mongo

const formatCPF = (digits) => {
    const d = digits.substring(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const formatCNPJ = (digits) => {
    const d = digits.substring(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const formatDocument = (value, type) => {
    const digits = value.replace(/\D/g, '');
    return type === 'CPF' ? formatCPF(digits) : formatCNPJ(digits);
};

const validateCPF = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += +d[i] * (10 - i);
    let r1 = (sum * 10) % 11;
    if (r1 >= 10) r1 = 0;
    if (r1 !== +d[9]) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += +d[i] * (11 - i);
    let r2 = (sum * 10) % 11;
    if (r2 >= 10) r2 = 0;
    return r2 === +d[10];
};

const validateCNPJ = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
    const calc = (str, len) => {
        let sum = 0, pos = len - 7;
        for (let i = len; i >= 1; i--) {
            sum += +str[len - i] * pos--;
            if (pos < 2) pos = 9;
        }
        const rem = sum % 11;
        return rem < 2 ? 0 : 11 - rem;
    };
    return calc(d, 12) === +d[12] && calc(d, 13) === +d[13];
};

const formatCep = (value) => {
    const d = value.replace(/\D/g, '').substring(0, 8);
    return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
};

const fileToDataURL = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const STEPS = ['Dados da empresa', 'Validar e-mail'];

const emptyForm = {
    email: '',
    documentType: 'CNPJ',
    document: '',
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
};

export default function ClientePendenteGuard({ children }) {
    const { bootstrap, loading: bootstrapLoading, refresh } = useBootstrap();
    const { pode } = usePermissao();
    // Override local: setado assim que validar-email responde 200, sem
    // depender do timing do refresh()/re-render do BootstrapContext. O
    // bootstrap real já confirma status=VALIDADO no corpo da resposta, mas
    // o context podia demorar/falhar a propagar isso pro guard a tempo.
    const [forceValidated, setForceValidated] = useState(false);

    const status = bootstrap?.client?.status;
    const isSuspenso = !forceValidated && status === 'SUSPENSO';
    const isPendente = !forceValidated && !isSuspenso && status !== 'VALIDADO';
    const clienteId = bootstrap?.client?.id;

    const handleValidated = async () => {
        setForceValidated(true);
        try {
            await refresh(true); // força fetch completo, sem ETag — evita 304 mascarar o novo status
        } catch (e) {
            console.error('Falha ao atualizar bootstrap após validação:', e);
        }
    };

    // Suspenso bloqueia todo mundo, sem exceção de role — não pode ser
    // contornado revalidando o e-mail (isso recolocaria o cliente como
    // VALIDADO e anularia a suspensão).
    // Aguarda revalidação do bootstrap antes de decidir mostrar o modal.
    // Evita flash quando o cache local tem status desatualizado (ex: PENDENTE→VALIDADO).
    if (bootstrapLoading) return children;

    if (isSuspenso) return <ContaSuspensa />;

    if (!isPendente) return children;

    // Conta pendente: quem pode completa o cadastro; todos os demais esperam.
    if (!pode('cliente.configurarCadastro')) return <AguardandoValidacao />;

    return (
        <>
            <ConfigurarModal clienteId={clienteId} initialData={bootstrap?.client} onSuccess={handleValidated} />
            <Box sx={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none' }}>
                {children}
            </Box>
        </>
    );
}

function ContaSuspensa() {
    const theme = useTheme();
    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100vh', gap: 2, px: 3, textAlign: 'center' }}
        >
            <Lucide.Ban size={64} color={theme.palette.error.main} />
            <Typography variant="h5" fontWeight={700}>
                Conta suspensa
            </Typography>
            <Typography color="text.secondary" maxWidth={480}>
                O acesso a esta conta foi suspenso. Entre em contato com o suporte
                para mais informações.
            </Typography>
        </Box>
    );
}

function AguardandoValidacao() {
    const theme = useTheme();
    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100vh', gap: 2, px: 3, textAlign: 'center' }}
        >
            <Lucide.Hourglass size={64} color={theme.palette.warning.main} />
            <Typography variant="h5" fontWeight={700}>
                Aguardando validação
            </Typography>
            <Typography color="text.secondary" maxWidth={480}>
                O gestor ou administrador ainda não concluiu a configuração do ambiente.
                Você terá acesso assim que o cadastro for validado.
            </Typography>
        </Box>
    );
}

function ConfigurarModal({ clienteId, initialData, onSuccess }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [step, setStep] = useState(0);
    const [form, setForm] = useState(() => {
        const docType = initialData?.documentType || 'CNPJ';
        return {
            ...emptyForm,
            email:        initialData?.email        || '',
            documentType: docType,
            document:     formatDocument(initialData?.document || '', docType),
            zipCode:      formatCep(initialData?.zipCode || ''),
            street:       initialData?.street       || '',
            number:       initialData?.number       || '',
            complement:   initialData?.complement   || '',
            neighborhood: initialData?.neighborhood || '',
            city:         initialData?.city         || '',
            state:        initialData?.state        || '',
        };
    });
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [cepLoading, setCepLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [logoBase64, setLogoBase64] = useState(initialData?.logoBase64 || null);
    const [emailBackgroundBase64, setEmailBackgroundBase64] = useState(initialData?.emailBackgroundBase64 || null);

    const clearFieldError = (name) => {
        if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
        clearFieldError(name);
        setError('');
    };

    const handleDocumentTypeChange = (e) => {
        const newType = e.target.value;
        setForm((f) => ({
            ...f,
            documentType: newType,
            document: formatDocument(f.document, newType),
        }));
        clearFieldError('document');
        setError('');
    };

    const handleDocumentChange = (e) => {
        setForm((f) => ({ ...f, document: formatDocument(e.target.value, f.documentType) }));
        clearFieldError('document');
        setError('');
    };

    const handleCepChange = async (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        setForm((f) => ({ ...f, zipCode: formatCep(raw) }));
        setError('');
        if (raw.length === 8) {
            setCepLoading(true);
            try {
                const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
                const data = await res.json();
                if (!data.erro) {
                    setForm((f) => ({
                        ...f,
                        street:       data.logradouro || f.street,
                        neighborhood: data.bairro     || f.neighborhood,
                        city:         data.localidade || f.city,
                        state:        data.uf         || f.state,
                    }));
                }
            } catch {
                // falha silenciosa — usuário preenche manualmente
            } finally {
                setCepLoading(false);
            }
        }
    };

    const handleImageUpload = async (e, setter) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Selecione um arquivo de imagem.');
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setError('Imagem muito grande (máx. 1.5MB).');
            return;
        }
        try {
            setter(await fileToDataURL(file));
            setError('');
        } catch {
            setError('Erro ao ler a imagem.');
        }
    };

    const handleSalvar = async () => {
        const erros = {};

        const emailVal = form.email?.trim();
        if (!emailVal) {
            erros.email = 'Campo obrigatório';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailVal)) {
            erros.email = 'Formato de e-mail inválido';
        }

        if (!form.document?.trim()) {
            erros.document = 'Campo obrigatório';
        } else {
            const docValido = form.documentType === 'CPF'
                ? validateCPF(form.document)
                : validateCNPJ(form.document);
            if (!docValido) erros.document = `${form.documentType} inválido — verifique os dígitos`;
        }

        if (!form.street?.trim())  erros.street  = 'Campo obrigatório';
        if (!form.city?.trim())    erros.city    = 'Campo obrigatório';
        if (!form.state?.trim())   erros.state   = 'Campo obrigatório';

        if (Object.keys(erros).length > 0) {
            setFieldErrors(erros);
            return;
        }

        setFieldErrors({});
        setLoading(true);
        setError('');
        try {
            // Envia documento e CEP sem máscara
            const payload = {
                ...form,
                document: form.document.replace(/\D/g, ''),
                zipCode:  form.zipCode.replace(/\D/g, ''),
            };
            await configurarCliente(clienteId, payload);
            if (logoBase64 || emailBackgroundBase64) {
                // Branding é opcional — falha aqui não deve bloquear a validação do e-mail.
                try {
                    await updateClienteBranding(clienteId, { logoBase64, emailBackgroundBase64 });
                } catch (brandingErr) {
                    console.error('Falha ao salvar branding:', brandingErr);
                }
            }
            setStep(1);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Erro ao salvar dados.');
        } finally {
            setLoading(false);
        }
    };

    const handleValidar = async () => {
        if (!code.trim()) {
            setError('Digite o código recebido por e-mail.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await validarEmailCliente(clienteId, code.trim());
            await onSuccess();
        } catch (e) {
            setError(e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Código inválido ou expirado.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    m: isMobile ? 0 : 2,
                },
            }}
        >
            <DialogTitle sx={{ pb: 0, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Lucide.BadgeCheck size={18} color={theme.palette.primary.main} />
                    <Typography variant="h6" fontWeight={700} component="span">
                        Configure seu ambiente
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 3, sm: 3 }, overflowY: 'auto' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, mt: 1 }}>
                    Complete o cadastro para liberar o acesso à plataforma.
                </Typography>

                <Stepper activeStep={step} sx={{ mb: 3 }}>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel
                                sx={{
                                    '& .MuiStepLabel-label': { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {step === 0 && (
                    <Box display="flex" flexDirection="column" gap={1.5}>

                        {/* ── Identificação ── */}
                        <TextField
                            label="E-mail *"
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleFormChange}
                            fullWidth
                            size="small"
                            error={!!fieldErrors.email}
                            helperText={fieldErrors.email}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            <FormControl size="small" sx={{ flex: '1 1 130px' }}>
                                <InputLabel>Tipo de documento *</InputLabel>
                                <Select
                                    name="documentType"
                                    value={form.documentType}
                                    label="Tipo de documento *"
                                    onChange={handleDocumentTypeChange}
                                >
                                    <MenuItem value="CNPJ">CNPJ</MenuItem>
                                    <MenuItem value="CPF">CPF</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label={`${form.documentType} *`}
                                name="document"
                                value={form.document}
                                onChange={handleDocumentChange}
                                size="small"
                                inputProps={{
                                    maxLength: form.documentType === 'CPF' ? 14 : 18,
                                    inputMode: 'numeric',
                                }}
                                sx={{ flex: '2 1 160px' }}
                                error={!!fieldErrors.document}
                                helperText={fieldErrors.document}
                            />
                        </Box>

                        {/* ── Endereço ── */}
                        <Divider sx={{ my: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                                Endereço
                            </Typography>
                        </Divider>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            <TextField
                                label="CEP"
                                name="zipCode"
                                value={form.zipCode}
                                onChange={handleCepChange}
                                size="small"
                                inputProps={{ maxLength: 9, inputMode: 'numeric' }}
                                sx={{ flex: '0 1 110px', minWidth: 90 }}
                                InputProps={{
                                    endAdornment: cepLoading ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={13} />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <TextField
                                label="Rua / Logradouro *"
                                name="street"
                                value={form.street}
                                onChange={handleFormChange}
                                size="small"
                                sx={{ flex: '1 1 160px' }}
                                error={!!fieldErrors.street}
                                helperText={fieldErrors.street}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            <TextField
                                label="Número"
                                name="number"
                                value={form.number}
                                onChange={handleFormChange}
                                size="small"
                                sx={{ flex: '0 1 90px', minWidth: 72 }}
                            />
                            <TextField
                                label="Complemento"
                                name="complement"
                                value={form.complement}
                                onChange={handleFormChange}
                                size="small"
                                sx={{ flex: '1 1 140px' }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            <TextField
                                label="Bairro"
                                name="neighborhood"
                                value={form.neighborhood}
                                onChange={handleFormChange}
                                size="small"
                                sx={{ flex: '1 1 120px' }}
                            />
                            <TextField
                                label="Cidade *"
                                name="city"
                                value={form.city}
                                onChange={handleFormChange}
                                size="small"
                                sx={{ flex: '2 1 140px' }}
                                error={!!fieldErrors.city}
                                helperText={fieldErrors.city}
                            />
                            <TextField
                                label="UF *"
                                name="state"
                                value={form.state}
                                onChange={handleFormChange}
                                size="small"
                                inputProps={{ maxLength: 2 }}
                                sx={{ flex: '0 1 64px', minWidth: 56 }}
                                error={!!fieldErrors.state}
                                helperText={fieldErrors.state}
                            />
                        </Box>

                        {/* ── Branding (opcional) ── */}
                        <Divider sx={{ my: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                                Identidade visual · opcional
                            </Typography>
                        </Divider>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                                    Logo do app
                                </Typography>
                                <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.75, fontSize: '0.68rem', textAlign: 'center' }}>
                                    360 × 80 px · PNG com fundo transparente · prefira versão com contraste para fundo escuro
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                        sx={{
                                            width: 140, height: 36, borderRadius: 1,
                                            border: '1px dashed', borderColor: 'divider',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', flexShrink: 0,
                                        }}
                                    >
                                        {logoBase64 ? (
                                            <Box component="img" src={logoBase64} alt="Logo" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                        ) : (
                                            <Lucide.Image size={18} color={theme.palette.action.disabled} />
                                        )}
                                    </Box>
                                    <Button component="label" size="small" startIcon={<Lucide.Upload />}>
                                        Enviar
                                        <input type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, setLogoBase64)} />
                                    </Button>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                                    Background do e-mail
                                </Typography>
                                <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.75, fontSize: '0.68rem', textAlign: 'center' }}>
                                    600 × 200 px · JPG ou PNG · banner do cabeçalho
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                        sx={{
                                            width: 44, height: 44, borderRadius: 1,
                                            border: '1px dashed', borderColor: 'divider',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', flexShrink: 0,
                                        }}
                                    >
                                        {emailBackgroundBase64 ? (
                                            <Box component="img" src={emailBackgroundBase64} alt="Background" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                        ) : (
                                            <Lucide.Image size={18} color={theme.palette.action.disabled} />
                                        )}
                                    </Box>
                                    <Button component="label" size="small" startIcon={<Lucide.Upload />}>
                                        Enviar
                                        <input type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, setEmailBackgroundBase64)} />
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        {error && (
                            <Typography color="error" variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {error}
                            </Typography>
                        )}

                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleSalvar}
                            disabled={loading}
                            fullWidth
                            endIcon={loading && <CircularProgress size={16} />}
                            sx={{ mt: 0.5 }}
                        >
                            Salvar e enviar código
                        </Button>
                    </Box>
                )}

                {step === 1 && (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2">
                            Um código de 6 dígitos foi enviado para <strong>{form.email}</strong>.
                            Digite-o abaixo para confirmar e liberar o acesso.
                        </Typography>
                        <TextField
                            label="Código de validação *"
                            value={code}
                            onChange={(e) => { setCode(e.target.value); setError(''); }}
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                            sx={{
                                '& input': {
                                    letterSpacing: { xs: '0.25em', sm: '0.45em' },
                                    fontSize: { xs: '1.1rem', sm: '1.4rem' },
                                    textAlign: 'center',
                                },
                            }}
                        />

                        {error && (
                            <Typography color="error" variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {error}
                            </Typography>
                        )}

                        <Box display="flex" gap={1} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Button
                                variant="outlined"
                                size="medium"
                                onClick={() => { setStep(0); setError(''); }}
                                sx={{ flexShrink: 0 }}
                            >
                                Voltar
                            </Button>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={handleValidar}
                                disabled={loading}
                                endIcon={loading && <CircularProgress size={16} />}
                                fullWidth
                            >
                                Validar e acessar
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
